import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRole, isAdmin as isAdminRole } from '@/contexts/RoleContext';
import { toast } from 'sonner';

export interface Ticket {
  id: string;
  client_id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  client_name?: string;
}

export interface TicketMessage {
  id: string;
  ticket_id: string;
  sender_id: string;
  sender_type: 'client' | 'admin';
  sender_name: string;
  message: string;
  is_internal: boolean;
  attachment_url: string | null;
  attachment_name: string | null;
  created_at: string;
}

export function useTickets() {
  const { role, clientId } = useRole();
  // Explicit 4-role check via helper (Phase 16-07). Team roles (setter/closer) go
  // down the client path here — they scope to their own clientId... actually team
  // roles have no clientId, so the `!isAdmin && clientId` branch yields an empty
  // query. That's the safe default until team-scoped ticket filtering is designed.
  const isAdmin = isAdminRole(role);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('support_tickets')
        .select('*, clients(company_name)')
        .order('updated_at', { ascending: false });

      if (!isAdmin && clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query;
      if (error) throw error;

      setTickets((data || []).map((t: any) => ({
        ...t,
        client_name: t.clients?.company_name || 'Unknown',
      })));
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, clientId]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const createTicket = async (data: {
    subject: string;
    category: string;
    priority: string;
    message: string;
    attachmentFile?: File;
  }) => {
    if (!clientId) throw new Error('No client ID');

    const { data: clientData } = await supabase
      .from('clients')
      .select('company_name, contact_name')
      .eq('id', clientId)
      .single();

    const senderName = clientData?.contact_name || clientData?.company_name || 'Client';

    const { data: ticket, error: ticketError } = await supabase
      .from('support_tickets')
      .insert({
        client_id: clientId,
        subject: data.subject,
        category: data.category,
        priority: data.priority,
      })
      .select()
      .single();

    if (ticketError) throw ticketError;

    let attachmentUrl: string | null = null;
    let attachmentName: string | null = null;
    if (data.attachmentFile) {
      const result = await uploadAttachment(clientId, ticket.id, data.attachmentFile);
      attachmentUrl = result.url;
      attachmentName = result.name;
    }

    const { data: { session } } = await supabase.auth.getSession();

    const { error: msgError } = await supabase
      .from('ticket_messages')
      .insert({
        ticket_id: ticket.id,
        sender_id: session?.user.id || clientId,
        sender_type: 'client',
        sender_name: senderName,
        message: data.message,
        attachment_url: attachmentUrl,
        attachment_name: attachmentName,
      });

    if (msgError) throw msgError;

    // Fire-and-forget Slack notification
    supabase.functions.invoke('support-ticket-notify', {
      body: {
        action: 'new_ticket',
        ticketId: ticket.id,
        subject: data.subject,
        category: data.category,
        priority: data.priority,
        clientName: senderName,
      },
    }).catch(() => {});

    await fetchTickets();
    return ticket;
  };

  return { tickets, loading, fetchTickets, createTicket };
}

export function useTicketMessages(ticketId: string | undefined) {
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // Real-time subscription for new messages
  useEffect(() => {
    if (!ticketId) return;

    const channel = supabase
      .channel(`ticket-${ticketId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'ticket_messages',
        filter: `ticket_id=eq.${ticketId}`,
      }, (payload) => {
        const newMsg = payload.new as TicketMessage;
        setMessages(prev => {
          // Avoid duplicates (message might already be in state from optimistic update)
          if (prev.some(m => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [ticketId]);

  const sendMessage = async (data: {
    message: string;
    senderType: 'client' | 'admin';
    senderName: string;
    isInternal?: boolean;
    attachmentFile?: File;
  }) => {
    if (!ticketId) return;

    let attachmentUrl: string | null = null;
    let attachmentName: string | null = null;
    if (data.attachmentFile) {
      const { data: ticket } = await supabase
        .from('support_tickets')
        .select('client_id')
        .eq('id', ticketId)
        .single();

      if (ticket) {
        const result = await uploadAttachment(ticket.client_id, ticketId, data.attachmentFile);
        attachmentUrl = result.url;
        attachmentName = result.name;
      }
    }

    const { data: { session } } = await supabase.auth.getSession();

    const { error } = await supabase
      .from('ticket_messages')
      .insert({
        ticket_id: ticketId,
        sender_id: session?.user.id || '',
        sender_type: data.senderType,
        sender_name: data.senderName,
        message: data.message,
        is_internal: data.isInternal || false,
        attachment_url: attachmentUrl,
        attachment_name: attachmentName,
      });

    if (error) throw error;

    // Update ticket's updated_at
    await supabase
      .from('support_tickets')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', ticketId);
  };

  const updateStatus = async (status: string) => {
    if (!ticketId) return;

    const update: Record<string, any> = {
      status,
      updated_at: new Date().toISOString(),
    };

    if (status === 'resolved' || status === 'closed') {
      const { data: { session } } = await supabase.auth.getSession();
      update.resolved_at = new Date().toISOString();
      update.resolved_by = session?.user.id;
    }

    const { error } = await supabase
      .from('support_tickets')
      .update(update)
      .eq('id', ticketId);

    if (error) throw error;

    supabase.functions.invoke('support-ticket-notify', {
      body: { action: 'status_change', ticketId, status },
    }).catch(() => {});
  };

  return { messages, loading, fetchMessages, sendMessage, updateStatus };
}

async function uploadAttachment(
  clientId: string,
  ticketId: string,
  file: File
): Promise<{ url: string; name: string }> {
  const ext = file.name.split('.').pop() || 'bin';
  const fileName = `${crypto.randomUUID()}.${ext}`;
  const path = `${clientId}/${ticketId}/${fileName}`;

  const { error } = await supabase.storage
    .from('support-attachments')
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw new Error(`Upload failed: ${error.message}`);

  // Store the storage path (not a signed URL) — signed URLs are generated fresh at render time
  return { url: path, name: file.name };
}
